import { encounterField, encounterCreatureField, inventoryField, itemsCol, posCoord } from '../../shared/array-types.js';
import { distanceToZoneFt, inventoryFootprint, inventoryHeightFt, fallingShapeSupportFloor, supportZAtOrBelow, type RestRow, type RestShape, type FootprintInputs } from '../inventory/floor-move.js';
import { SIZE_WIDTHS_FT } from '../inventory/lift-occupancy.js';
import { isAloftUnderOwnFlight, speedFeetFromCluster } from '../../shared/hunker-visibility.js';
import { readCreatureStatuses } from '../compile/creature-status-reader.js';
import { statusItemId, statusCasterId, statusRemovalBefore } from '../statuses/expiry.js';
import type { GmBoundaryVerdict } from '../statuses/gm-boundary.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row => v && typeof v === 'object' && !Array.isArray(v) ? v as Row : {};
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
export const HUNKER_ABILITY = '8C28EBE3-60EA-D493-0FB8-67859CE92F63';
export const HUNKER_STATUS = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';
export interface HunkerObject {
  object: Row;
  footprint: FootprintInputs;
  shape: RestShape | null;
  supportRows?: () => Iterable<RestRow>;
}
const point = (row: Row) => ({ x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 });
export const hunkerStatuses = (row: Row): Row[] => Object.values(readCreatureStatuses(row)).filter(status =>
  statusItemId(status as Row) === HUNKER_STATUS && Number((status as Row).status_deleted ?? 0) === 0) as Row[];

/** One rule for radial availability and the cast itself, on held physics and inventory geometry. */
export function hunkerUnavailableReason(encounter: Row, actor: Row, input: HunkerObject | null | undefined,
  value: (row: Row, key: string) => unknown): string | null {
  if (!input || !inventoryField(input.object, 'inventory_id')
    || upper(inventoryField(input.object, 'possess_id')) !== upper(encounterField(encounter, 'encounter_id'))
    || Number(inventoryField(input.object, 'inventory_deleted') ?? 0) !== 0) return 'Choose an object on the encounter floor to hunker behind.';
  if (hunkerStatuses(actor).length) return 'End Hunker Down before choosing another object.';
  const size = record(record(record(actor.computed).creature).size), height = Number(size.height), width = Number(size.base);
  if (!(height > 0) || !(width > 0)) return 'Your creature’s physical size is still loading.';
  const { object, footprint, shape } = input, at = point(actor);
  const name = String(inventoryField(object, 'inventory_name') || itemsCol(object, 'item_name') || 'object');
  const objectHeight = inventoryHeightFt(footprint, id => SIZE_WIDTHS_FT[id] ?? null);
  if (objectHeight + 1e-6 < height / 2) return `The ${name} is too short: cover must be at least half your height.`;
  let support = shape ? fallingShapeSupportFloor(shape, at.x, at.y, at.z, 2) ?? 0 : 0;
  const speed = (key: string) => speedFeetFromCluster(record(record(record(actor.computed).creature).speeds), key) ?? Number(value(actor, key) ?? 0);
  const aloft = () => isAloftUnderOwnFlight(at.z - support + height / 2, height,
    encounterCreatureField(actor.encounter_creatures, 'preferred_speed_type'), speed('speed_flying'), speed('speed_hovering'));
  // Query only supporting rows when flight is otherwise aloft; never assemble/rebuild an obstacle world.
  if (aloft()) {
    for (const row of input.supportRows?.() ?? []) {
      support = Math.max(support, supportZAtOrBelow({ shape: null, depthAuthored: true, rows: [row] }, at.x, at.y, at.z, null, 2) ?? support);
      if (!aloft()) break;
    }
    if (aloft()) return 'Land before hunkering behind an object.';
  }
  const zone = inventoryFootprint(footprint, id => SIZE_WIDTHS_FT[id] ?? null);
  const horizontal = Math.max(0, distanceToZoneFt(zone, at.x, at.y)[0] - width / 2);
  const vertical = Math.max(0, footprint.z - (at.z + height), at.z - (footprint.z + objectHeight));
  const reach = Number(value(actor, 'reach') ?? 5);
  if (!Number.isFinite(reach) || Math.hypot(horizontal, vertical) > reach + 1e-6) return `Move within reach of the ${name} first.`;
  return null;
}

/** Existing saved columns bind the posture to this exact floor row and its position; no schema change. */
export function bindHunkerStatus(status: Row, input: HunkerObject): void {
  const at = point(input.object);
  Object.assign(status, { caster: upper(inventoryField(input.object, 'inventory_id')),
    status_distance_point_x: at.x, status_distance_point_y: at.y, status_distance_point_z: at.z });
}

/** A changed/removed cover row ends only the postures bound to it, even if its new position is still nearby. */
export function brokenObjectHunkers(encounter: Row, changedIds: ReadonlySet<string>): GmBoundaryVerdict['statusesRemoved'] {
  const floor = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [];
  return (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []).flatMap(actor => {
    const target = upper(encounterCreatureField(actor.encounter_creatures, 'encounter_creature_id'));
    return hunkerStatuses(actor).filter(status => {
      const id = statusCasterId(status);
      if (!changedIds.has(id)) return false;
      const object = floor.find(row => upper(inventoryField(row, 'inventory_id')) === id);
      if (!object || Number(inventoryField(object, 'inventory_deleted') ?? 0) !== 0
        || upper(inventoryField(object, 'possess_id')) !== upper(encounterField(encounter, 'encounter_id'))) return true;
      const at = point(object);
      return ['x', 'y', 'z'].some(axis => Math.abs(Number(status[`status_distance_point_${axis}`] ?? 0) - at[axis as keyof typeof at]) > 1e-6);
    }).map(status => ({ status_id: upper(status.status_id), status_item_id: HUNKER_STATUS, target, before: statusRemovalBefore(status, target) }));
  });
}
